import { type FC, type ReactNode } from "react";
import { datadogLogs } from "@datadog/browser-logs";
import { Box, Button, Stack, Typography } from "@mui/material";
import * as Sentry from "@sentry/react";
import ErrorBoundaryImg from "assets/backgrounds/error_boundary.jpg";
import PlugLogoWhite from "assets/logos/plug_logo_white.png";
import { FormattedMessage } from "react-intl";

// eslint-disable-next-line @typescript-eslint/unbound-method -- resetError type is controlled by Sentry's FallbackRender
const ErrorFallback: Sentry.FallbackRender = ({ resetError, eventId }) => {
  return (
    <Stack sx={{ width: "100vw", height: "100vh" }}>
      <Box
        display="flex"
        height="100%"
        style={{
          backgroundImage: `url(${ErrorBoundaryImg})`,
          backgroundSize: "cover",
          backgroundPosition: "bottom",
          backgroundRepeat: "no-repeat",
        }}
      >
        <Stack
          width="100%"
          alignItems="center"
          sx={{ textAlign: "center", color: "white", mt: 4 }}
        >
          <img
            style={{ width: 250, objectFit: "contain" }}
            alt="Plug"
            src={PlugLogoWhite}
          />
          <Typography variant="h1" fontWeight="boldest" mb={6}>
            <FormattedMessage defaultMessage="Looks like something went wrong" />
          </Typography>

          <Typography variant="h4">
            <FormattedMessage defaultMessage="Our team have been informed about this" />
          </Typography>
          <Typography variant="h4" mt={3}>
            <FormattedMessage defaultMessage="Until we resolve this issue you can" />
          </Typography>

          <Stack direction="row" alignItems="center" spacing={1} mt={3}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                window.location.reload();
              }}
            >
              <FormattedMessage defaultMessage="Refresh this page" />
            </Button>
            <span>
              <FormattedMessage defaultMessage="or" />
            </span>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                resetError();
                window.location.href = "/dashboard";
              }}
            >
              <FormattedMessage defaultMessage="Go to dashboard" />
            </Button>
            <span>
              <FormattedMessage defaultMessage="or" />
            </span>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                Sentry.showReportDialog({ eventId });
              }}
            >
              <FormattedMessage defaultMessage="Report feedback" />
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Stack>
  );
};

const GlobalErrorBoundary: FC<{ children: ReactNode }> = ({ children }) => {
  return (
    <Sentry.ErrorBoundary
      fallback={ErrorFallback}
      onError={(error, componentStack, eventId) => {
        const err = error as Error;
        datadogLogs.logger.error("Global Error Boundary", {
          errorName: err.name,
          errorMessage: err.message,
          errorStack: err.stack,
          componentStack,
          eventId,
        });
      }}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
};

export default GlobalErrorBoundary;
